import type { AvailableJourneysForSegment } from '@massilia/queries'

import { cn } from '@massilia/ui'
import { Clock, Plane } from 'lucide-react'

import { formatDuration } from '@/utils/format-duration'

import { StopsIndicator } from './stops-indicator'

interface JourneyOptionsPanelProps {
  availableJourneys: AvailableJourneysForSegment[]
  onSelectionChange: (segmentIndex: number, journeyKey: string) => void
  selectedJourneyKeys: string[]
}

interface SegmentOptionsProps {
  onSelect: (journeyKey: string) => void
  segment: AvailableJourneysForSegment
  selectedKey: string | undefined
}

export function JourneyOptionsPanel({
  availableJourneys,
  onSelectionChange,
  selectedJourneyKeys,
}: JourneyOptionsPanelProps) {
  // Don't render if no options to show
  if (availableJourneys.length === 0) {
    return null
  }

  return (
    <div className="space-y-4 border-t border-gray-200 bg-gray-50 p-4">
      <h3 className="text-sm font-semibold text-gray-700">Choisissez vos horaires</h3>

      {availableJourneys.map((segment) => (
        <SegmentOptions
          key={segment.segmentIndex}
          onSelect={(journeyKey) => {
            onSelectionChange(segment.segmentIndex, journeyKey)
          }}
          segment={segment}
          selectedKey={selectedJourneyKeys[segment.segmentIndex - 1]}
        />
      ))}
    </div>
  )
}

function SegmentOptions({ onSelect, segment, selectedKey }: SegmentOptionsProps) {
  return (
    <div className="space-y-2">
      {/* Segment header */}
      <div className="flex flex-row flex-wrap items-center gap-2 text-sm font-medium text-gray-600">
        <Plane className="size-4" />
        <span>{segment.segmentLabel}</span>
        <span className="text-xs text-gray-400">({segment.options.length} options)</span>
      </div>

      {/* Options list */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {segment.options.map((option) => {
          const isSelected = selectedKey === option.journeyKey
          const durationStr = formatDuration(option.duration)

          return (
            <button
              className={cn(
                'flex flex-col rounded-lg border p-3 text-left transition-all hover:border-orange-300 hover:bg-orange-50',
                isSelected
                  ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500'
                  : 'border-gray-200 bg-white'
              )}
              key={option.journeyKey}
              onClick={() => {
                onSelect(option.journeyKey)
              }}
              type="button"
            >
              {/* Times */}
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900">{option.departureTime}</span>
                <span className="text-gray-400">&rarr;</span>
                <span className="text-lg font-bold text-gray-900">
                  {option.arrivalTime}
                  {option.isNextDay && (
                    <span className="ml-1 text-xs font-normal text-orange-600">+1</span>
                  )}
                </span>
              </div>

              {/* Airports */}
              <div className="mt-1 text-xs text-gray-500">
                {option.departureAirport} &rarr; {option.arrivalAirport}
              </div>

              {/* Duration and stops */}
              <div className="mt-2 flex items-center gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="size-3" />
                  <span>{durationStr}</span>
                </div>
                <StopsIndicator legs={option.legs} stops={option.stops} variant="compact" />
              </div>

              {/* Flight number */}
              <div className="mt-1 text-xs text-gray-400">{option.flightNumber}</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
